import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons/faCheckCircle';
import SelectMultipleDropdown, { SelectDropdownOption } from 'components/Forms/ShareSendClients/SelectMultipleDropdown';
import { Drawer } from 'components/UI/V3/Drawer/Drawer';
import Button from 'components/UI/V3/Button/Button';
import { SearchComponent, Spinner } from 'components/UI';
import localStyles from './ShareSendClients.module.scss';
import { showErrorThunk } from '@spike/notifications-action';
import AutocompleteResult from 'model/AutocompleteResult';
import { useApiClientWrapper } from 'hooks';
import Channel from '../ui/Channel';
import MobileCloseButton from '../ui/MobileCloseButton';
import { sendFormsThunk, FormsStatus } from '@spike/forms-action';
import useNonInitialEffect from '@versiondos/hooks';
import { searchClientsThunk, reset } from '@spike/clients-action';
import Client from '@spike/client-model';
import { SelectedClient } from 'components/Agreements/Send/model';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    initialFormId?: number;
    onSent?: () => void;
};

const ShareSendClients: FunctionComponent<Props> = ({ isOpen, onClose, initialFormId, onSent }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const formsSlice = useSelector((s: RootState) => (s as any).forms);
    const allForms = (formsSlice?.forms as any) || [];
    const formsStatus = useSelector<RootState, FormsStatus>(s => s.forms.status);

    const loading = useSelector<RootState, boolean>(s => s.clients.loading);
    const clientsList = useSelector<RootState, Array<Client>>(state => state.clients.list);

    // Build options for SelectDropdown
    const formOptions: Array<SelectDropdownOption<number>> = useMemo(() => {
        return allForms
            .filter((f: any) => f.active)
            .map((f: any) => ({
                id: f.id as number,
                label: f.title || f.name || `Form ${f.id}`
            }));
    }, [allForms]);

    const [selectedForms, setSelectedForms] = useState<Array<SelectDropdownOption<number>>>([]);

    // If an initialFormId is provided (opened from a specific form), preselect it when options are available
    useEffect(() => {
        if (!isOpen) return;
        if (!initialFormId) return;
        if (!formOptions || formOptions.length === 0) return;

        const match = formOptions.find(o => o.id === initialFormId);
        if (match) {
            setSelectedForms([match]);
        }
    }, [initialFormId, formOptions, isOpen]);

    const [query, setQuery] = useState('');
    const [searching, setSearching] = useState(false);
    const [searchRequested, setSearchRequested] = useState(false);

    const [selectedChannel, setSelectedChannel] = useState<'email' | 'sms'>('email');

    // Selected clients from search results
    const [selectedClients, setSelectedClients] = useState<Array<SelectedClient>>([]);

    // UI state for sending
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    // Debounce search input
    useEffect(() => {
        if (!query || query.trim().length < 2) {
            dispatch(reset());
            setSearching(false);
            return;
        }

        setSearching(true);
        setSearchRequested(true);

        dispatch(searchClientsThunk(apiClientWrapper, encodeURIComponent(query.trim()), 1, 20));

        //return () => clearTimeout(t);
    }, [query]);

    // When channel changes, reset client-related state (mirror ShareSendPanel behavior)
    useEffect(() => {
        setSelectedClients([]);
        setQuery('');
        dispatch(reset());
    }, [selectedChannel, dispatch]);

    useEffect(() => {
        setSearching(loading);
    }, [loading]);

    // Stop showing the searching spinner when clients results arrive
    useEffect(() => {
        if (clientsList && clientsList.length > 0) {
            setSearching(false);
        }
        // if query is cleared, ensure not searching
        if (!query || query.trim().length < 2) {
            setSearching(false);
        }
        // if a search was requested but results arrived empty, stop spinner as well
        if (searchRequested && clientsList !== undefined) {
            setSearching(false);
            setSearchRequested(false);
        }
    }, [clientsList, query]);

    // Map clients list from store into a uniform AutocompleteResult-like shape
    const clientMatches: Array<SelectedClient & { email?: string; phone?: string }> = useMemo(() => {
        return (clientsList || []).map((c: any) => ({
            id: c.id,
            type: 'Customer',
            name: c.name || `${c.first_name || c.firstName || ''} ${c.last_name || c.lastName || ''}`.trim(),
            petId: undefined,
            email: c.email,
            phone: c.phone
        }));
    }, [clientsList]);

    const toggleSelectClient = (client: SelectedClient) => {
        const exists = selectedClients.some(c => c.id === client.id);
        if (exists) {
            setSelectedClients(prev => prev.filter(c => !(c.id === client.id)));
        } else {
            setSelectedClients(prev => [...prev, client]);
        }
    };

    const handleSend = () => {
        if (selectedForms.length === 0 || selectedClients.length === 0) return;

        const payload = {
            sentVia: selectedChannel as 'email' | 'sms',
            formIds: selectedForms.map(f => String(f.id)),
            customerIds: selectedClients.map(c => String(c.id))
        };

        dispatch(sendFormsThunk(apiClientWrapper as any, payload as any));
    };

    const resetState = () => {
        setSelectedForms([]);
        setSelectedClients([]);
        setQuery('');
        setSent(false);
    };

    const handleDone = () => {
        resetState();
        dispatch(reset());

        try {
            onSent && onSent();
        } catch (e) {
            console.error('onSent handler threw', e);
        }
        onClose();
    };

    const handleClose = () => {
        resetState();

        onClose();
    };

    // Follow ShareSendPanel pattern: react to global forms status to show success screen
    useNonInitialEffect(() => {
        if (formsStatus === FormsStatus.SendingForms) {
            setSending(true);
        } else if (formsStatus === FormsStatus.SendFormsSuccess) {
            setSending(false);
            setSent(true);
        } else if (formsStatus === FormsStatus.SendFormsError) {
            setSending(false);
            dispatch(showErrorThunk('Error sending form.'));
        }
    }, [formsStatus]);

    const closeButton = <MobileCloseButton onClick={sent ? handleDone : onClose} />;

    // Render success content
    if (!isOpen) return null;

    if (sent) {
        return (
            <Drawer onClose={sent ? handleDone : handleClose}>
                <div className={localStyles.resultsPanel}>
                    <div className={localStyles.header}>
                        <h2 className={localStyles.title}>Share & Send Form</h2>
                        {closeButton}
                    </div>

                    <div className={localStyles.containerResults}>
                        <FontAwesomeIcon
                            icon={faCheckCircle}
                            color="#5E8677"
                            fontSize={72}
                        />
                        <span className={localStyles.titleResults}>Form(s) Sent</span>
                        <p className={localStyles.successMessage}>
                            The form(s) were successfully sent to{' '}
                            <strong className={localStyles.successCount}>{selectedClients.length}</strong> client(s).
                        </p>
                    </div>
                </div>
                <div className={localStyles.footer}>
                    <Button
                        label="Done"
                        onClick={handleDone}
                        variant="black"
                        size="large"
                        fullWidth={true}
                    />
                </div>
            </Drawer>
        );
    }

    return (
        <Drawer onClose={handleClose}>
            <div className={localStyles.wrapper}>
                <div className={localStyles.panel}>
                    <div className={localStyles.header}>
                        <h3 className={localStyles.title}>Share & Send Form</h3>
                        {closeButton}
                    </div>
                    <div className={localStyles.subtitle}>
                        Share your intake form link today to connect with clients and efficiently create their profiles
                    </div>

                    <div className={localStyles.channelWrapper}>
                        <Channel
                            selectedChannel={selectedChannel}
                            onChannelChange={setSelectedChannel}
                        />
                    </div>
                    <div className={localStyles.sectionTitle}>Forms</div>
                    <div className={localStyles.sectionDescription}>Select multiple forms.</div>

                    <div className={localStyles.formsWrapper}>
                        <SelectMultipleDropdown
                            label="Forms"
                            options={formOptions}
                            showSelectAll={true}
                            value={selectedForms}
                            onChange={(v: any) => setSelectedForms(v || [])}
                            buttonProps={{
                                variant: 'outlined',
                                label:
                                    selectedForms.length === 1
                                        ? selectedForms[0].label
                                        : selectedForms.length > 1
                                        ? `${selectedForms.length} Selected`
                                        : 'Select Forms',
                                fullWidth: true
                            }}
                        />
                    </div>

                    <div className={localStyles.section}>
                        <div className={localStyles.sectionTitle}>Emails</div>
                        <div className={localStyles.sectionDescription}>Add multiple emails to send the form(s).</div>

                        <div className={localStyles.searchWrapper}>
                            <SearchComponent
                                open={true}
                                onSearch={(v: any) => setQuery(v)}
                                className={localStyles.search}
                                placeholder={'Search by client name, pet name, phone number or email'}
                            />

                            <div className={localStyles.searchResults}>
                                {searching && <Spinner />}

                                {!searching &&
                                    clientMatches &&
                                    clientMatches.length === 0 &&
                                    query.trim().length >= 2 && (
                                        <div className={localStyles.noResults}>No clients found</div>
                                    )}

                                {!searching &&
                                    ((clientMatches && clientMatches.length > 0) || selectedClients.length > 0) && (
                                        <div className={localStyles.resultsList}>
                                            {/* Build a deduplicated list: selected clients first, then parents that are not selected */}
                                            {/**
                                             * We'll create an array where selectedClients come first (to show Unselect)
                                             * and then parents filtered out by id/type so we don't duplicate entries.
                                             */}
                                            {(() => {
                                                const seen = new Set<string>();
                                                const rows: Array<SelectedClient> = [];

                                                // Add selected clients first
                                                selectedClients.forEach(sc => {
                                                    const key = `${sc.id}`;
                                                    if (!seen.has(key)) {
                                                        seen.add(key);
                                                        rows.push(sc);
                                                    }
                                                });

                                                (clientMatches || []).forEach(p => {
                                                    const key = `${p.id}`;
                                                    if (!seen.has(key)) {
                                                        seen.add(key);
                                                        rows.push(p);
                                                    }
                                                });

                                                return rows.map((p, idx) => {
                                                    const isSelected = selectedClients.some(c => c.id === p.id);
                                                    return (
                                                        <div
                                                            key={`${p.id}-${idx}`}
                                                            className={
                                                                isSelected
                                                                    ? localStyles.resultRowSelected
                                                                    : localStyles.resultRow
                                                            }
                                                        >
                                                            <div className={localStyles.resultRowDetails}>
                                                                <div className={localStyles.resultName}>{p.name}</div>
                                                                <div className={localStyles.resultContact}>
                                                                    {selectedChannel === 'email'
                                                                        ? p.email
                                                                            ? p.email
                                                                            : 'Client has no email.'
                                                                        : p.phone
                                                                        ? p.phone
                                                                        : 'Client has no phone.'}
                                                                </div>
                                                            </div>

                                                            <div className={localStyles.resultRowRight}>
                                                                <Button
                                                                    label={isSelected ? 'Unselect' : 'Select'}
                                                                    variant={'primary'}
                                                                    onClick={() => toggleSelectClient(p)}
                                                                    disabled={
                                                                        !isSelected &&
                                                                        ((selectedChannel === 'email' && !p.email) ||
                                                                            (selectedChannel === 'sms' && !p.phone))
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                    );
                                                });
                                            })()}
                                        </div>
                                    )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={localStyles.footer}>
                    <Button
                        label="Send"
                        variant="green"
                        disabled={selectedForms.length === 0 || selectedClients.length === 0 || sending}
                        fullWidth
                        onClick={handleSend}
                    />
                </div>
            </div>
        </Drawer>
    );
};

export default ShareSendClients;
